import React, { useState, useEffect } from "react";
import { Search, X, Clock } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import "./AllKeywordsModal.css";
import ApiService from "../../../services/Api.service";
import Pagination from "../../Pagination/Pagination";
import ConfirmDeleteModal from "../ConfirmDeleteModal/ConfirmDeleteModal";
import AuthService from "../../../services/Auth.service";
import Alert from "../../Alert/Alert";
import Button from "../../Button/Button";

const AllKeywordsModal = ({ onClose, keywords, setKeywords }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [dropDownData, setDropDownData] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertValue, setAlertValue] = useState({
    show: false,
    message: "",
    variant: "success",
    position: "middle",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    total: 0,
    total_pages: 0,
  });
  const [postsPerPage, setPostsPerPage] = useState(20);

  const [showAddPopup, setShowAddPopup] = useState(false);

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    defaultValues: {
      label: "",
      type: "",
      example: "",
    },
  });

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPostsPerPage(newSize);
    fetchDetail(1, newSize);
  };

  const fetchDropdownDetail = async () => {
    try {
      let subprocessRes = await ApiService.getKeywordsType();
      if (subprocessRes.status === 200) {
        setDropDownData(subprocessRes.data.keywords_type);
      }
    } catch (error) {
      console.error("Error fetching subprocesses:", error);
    }
  };

  useEffect(() => {
    fetchDropdownDetail();
  }, []);

  const fetchDetail = async (page = 1, size = postsPerPage) => {
    try {
      const payload = {
        keywords,
        pagination: { page, size },
      };
      let tableResData = await ApiService.getKeywords(payload);
      if (tableResData.status === 200 && tableResData.data) {
        setTableData(tableResData.data.keywords);
        setPagination(tableResData.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching subprocesses:", error);
    }
  };

  useEffect(() => {
    fetchDetail(1, postsPerPage);
  }, []);

  const searchKeyword = async (searchTerm) => {
    try {
      const formattedTerm = searchTerm.trim().replace(/\s+/g, "_")?.toLowerCase();
      let tableResData = await ApiService.searchKeyword(formattedTerm, keywords);
      if (tableResData.status === 200 && tableResData.data) {
        setTableData(tableResData.data.keywords);
      }
    } catch (error) {
      console.error("Error fetching subprocesses:", error);
    }
  };

  const suggestions = searchTerm
    ? tableData
      .map((kw) => kw.keyword)
      .filter(
        (kw, index, self) =>
          kw?.toLowerCase().includes(searchTerm?.toLowerCase()) &&
          self.indexOf(kw) === index
      )
      .slice(0, 5)
    : [];

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    searchKeyword(suggestion);
    setShowSuggestions(false);
    setRecentSearches((prev) => {
      const newList = [suggestion, ...prev.filter((item) => item !== suggestion)];
      return newList.slice(0, 5);
    });
  };

  const saveNewKeyword = async (data) => {
    try {
      const payload = { keyword: data.label.trim(), type: data.type, example: data.example.trim() };
      let res = await ApiService.addKeyword(payload);
      if (res.status === 200 || res.status === 201) {
        fetchDetail(pagination.page, postsPerPage);
        setShowAddPopup(false);
        reset();
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Something went wrong");
    }
  };

  const handleDeleteKeyword = async () => {
    if (!deleteTarget) return;
    try {
      const res = await AuthService.deleteKeyword(deleteTarget.id);
      if (res.status === 200) {
        setAlertValue({
          show: true,
          message: `${deleteTarget.name || "keyword"} deleted successfully!`,
          variant: "success",
          position: "middle",
        });
        fetchDetail(pagination.page, postsPerPage); // refresh
      }
    } catch (error) {
      console.error(error);
      setAlertValue({
        show: true,
        message:error.response?.data?.detail,
        variant: "danger",
        position: "middle",
      });
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      {alertValue.show && (
        <Alert
          variant={alertValue.variant}
          message={alertValue.message}
          dismissible={true}
          timeout={2500}
          position={alertValue.position}
          onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
        />
      )}
      <div className="audit-keyword-modal-overlay">
        <div className="audit-keyword-modal-content">
          <div className="audit-keyword-modal-header">
            <h3>All Keywords</h3>
            <button className="audit-keyword-modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          {/* 🔹 Search Bar */}
          <div className="audit-keyword-modal-search-bar">
            <div className="audit-keyword-search-wrapper">
              <Search size={16} className="search-icon" />
              <input
                placeholder="Search keyword or phrases"
                value={searchTerm}
                className="form-control"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  searchKeyword(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              />

              {showSuggestions && (
                <ul className="audit-keyword-suggestion-list">
                  {searchTerm === "" &&
                    recentSearches.map((item, index) => (
                      <li key={index} onMouseDown={() => handleSuggestionClick(item)}>
                        <Clock size={14} className="suggestion-icon" />
                        {item}
                      </li>
                    ))}
                  {searchTerm !== "" &&
                    suggestions.map((s, i) => (
                      <li key={i} onMouseDown={() => handleSuggestionClick(s)}>
                        <Search size={14} className="suggestion-icon" />
                        {s}
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {/* 🔹 Add New Button */}
            <button
              className="audit-keyword-modal-add-new"
              onClick={() => setShowAddPopup(true)}
            >
              + Add New
            </button>
          </div>

          {/* 🔹 Table */}
          <div className="audit-keyword-modal-table-wrapper">
            <table style={{ width: "100%", height: 300 }}>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Keyword</th>
                  <th>Keyword Type</th>
                  <th>Phrases Example</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tableData && tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={index}>
                      <td>{(pagination.page - 1) * pagination.size + index + 1}</td>
                      <td>{item.keyword}</td>
                      <td>{item.type}</td>
                      <td title={item.example ?? "--"}>{item.example}</td>
                      <td >
                        <button
                          className="audit-keyword-modal-add-new"
                          onClick={() => {
                            if (!keywords.includes(item.keyword)) {
                              const updatedKeywords = [...keywords, item.keyword];
                              setKeywords(updatedKeywords);
                              setTableData((prev) => prev.filter((kw) => kw.keyword !== item.keyword));
                            }
                          }}
                        >
                          + Add to used
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "10px" }}>
                      No matching keywords found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 🔹 Pagination */}
          <Pagination
            pagination={pagination}
            postsPerPage={postsPerPage}
            handlePageSizeChange={handlePageSizeChange}
            fetchDetail={fetchDetail}
          />

          {/* 🔹 Footer */}
          <div className="audit-keyword-modal-footer">
            <Button variant="primary" onClick={onClose}>
              Save & Close
            </Button>
          </div>
        </div>

        {/* 🔹 Add New Keyword Popup with React Hook Form */}
        {showAddPopup && (
          <div className="audit-keyword-popup-overlay">
            <div className="audit-keyword-popup">
              <div className="popup-header">
                <h5>Add New Keyword</h5>
                <button onClick={() => setShowAddPopup(false)} className="audit-keyword-modal-close">
                  <X size={20} />
                </button>
              </div>
              <form className="popup-body" onSubmit={handleSubmit(saveNewKeyword)}>
                <div className="mb-3">
                  <label htmlFor="keywordInput" className="form-label">Keyword</label>
                  <input
                    type="text"
                    id="keywordInput"
                    className="form-control"
                    placeholder="Keyword"
                    {...register("label", { required: "Keyword is required" })}
                  />
                  {errors.label && <span className="text-danger">{errors.label.message}</span>}
                </div>

                <div className="mb-3">
                  <label htmlFor="keywordType" className="form-label">Keyword Type</label>
                  <Controller
                    control={control}
                    name="type"
                    rules={{ required: "Keyword type is required" }}
                    render={({ field }) => (
                      <select {...field} id="keywordType" className="form-control">
                        <option value="">Select Keyword Type</option>
                        {dropDownData.map((kt) => (
                          <option key={kt.value} value={kt.value}>
                            {kt.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.type && <span className="text-danger">{errors.type.message}</span>}
                </div>

                <div className="mb-3">
                  <label htmlFor="exampleTextarea" className="form-label">Example</label>
                  <textarea
                    id="exampleTextarea"
                    className="form-control"
                    rows="3"
                    placeholder="Enter example phrases..."
                    {...register("example", {
                      required: "Example is required",
                      minLength: {
                        value: 5,
                        message: "Example must be at least 5 characters long",
                      },
                    })}
                  />
                  {errors.example && <span className="text-danger">{errors.example.message}</span>}
                </div>

                <div className="popup-footer">
                  <Button variant="outline" onClick={() => setShowAddPopup(false)}>Cancel</Button>
                  <Button variant="primary" type="submit">Save</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {showDeleteModal && (
        <ConfirmDeleteModal
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleDeleteKeyword}
          itemName={deleteTarget?.keyword || "delete Keyword"}
        />
      )}
    </>

  );
};

export default AllKeywordsModal;
